<template>
  <footer
    v-if="variants.main === variant"
    class="tw-flex tw-flex-col tw-text-center md:tw-text-start"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: var(--c-${vars.textColor});`"
  >
    <div
      class="tw-flex tw-flex-col tw-gap-14 tw-p-16 tw-pb-8 md:tw-flex-row md:tw-flex-wrap md:tw-justify-start 2xl:tw-gap-6 2xl:tw-px-48 2xl:tw-pt-28"
    >
      <div class="tw-flex tw-flex-col tw-gap-20 xl:tw-flex-row">
        <div class="tw-flex tw-max-w-5xl tw-flex-[30%] tw-flex-col tw-items-center tw-gap-5 md:tw-items-start">
          <NuxtImg :src="company.logo" class="tw-max-w-60" />
          <small class="tw-opacity-60">
            {{ vars.descriptionText }}
          </small>
        </div>
        <div class="tw-flex tw-flex-col tw-gap-14 md:tw-flex-row xl:tw-gap-32">
          <div class="tw-flex tw-flex-col tw-items-center tw-gap-5 md:tw-items-start">
            <h4 class="tw-whitespace-nowrap">{{ vars.columnTitle1Text }}</h4>
            <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 md:tw-items-start">
              <nuxt-link
                v-for="(column1, index) in columns1"
                :key="`column1-${index}`"
                :to="column1.link"
                class="n-link tw-opacity-60"
                :external="true"
                >{{ column1.item }}</nuxt-link
              >
            </div>
          </div>
          <div class="tw-flex tw-flex-col tw-items-center tw-gap-5 tw-whitespace-nowrap md:tw-items-start">
            <h4>{{ vars.columnTitle2Text }}</h4>
            <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 md:tw-items-start">
              <nuxt-link
                v-for="(column2, index) in columns2"
                :key="`column2-${index}`"
                :to="column2.link"
                class="n-link tw-opacity-60"
                :external="true"
                >{{ column2.item }}</nuxt-link
              >
            </div>
          </div>
          <div class="tw-flex tw-flex-col tw-items-center tw-gap-5 md:tw-items-start">
            <p class="tw-opacity-60">{{ vars.companyTradingAsText }}</p>
            <p class="tw-opacity-60">{{ vars.companyText }}</p>
            <p class="tw-opacity-60">Company Number: {{ vars.companyNumber }}</p>
            <p class="tw-opacity-60">Registered Address: {{ vars.companyAddressText }}</p>
            <div>
              <p class="tw-opacity-60">Redress Scheme:</p>
              <div class="tw-flex tw-flex-col tw-gap-2">
                <nuxt-link
                  :external="true"
                  :to="vars.companyRedressSchemeLinkUrl"
                  class="n-link tw-pb-2 tw-opacity-60"
                  target="_blank"
                >
                  {{ vars.companyRedressSchemeText }}
                </nuxt-link>
                <nuxt-link
                  :external="true"
                  :to="vars.companyRedressScheme2LinkUrl"
                  class="n-link tw-opacity-60"
                  target="_blank"
                >
                  {{ vars.companyRedressScheme2Text }}
                </nuxt-link>
              </div>
            </div>

            <p class="tw-opacity-60">
              <nuxt-link :external="true" :to="vars.companyRedressSchemeLinkUrl" class="n-link" target="_blank">
                {{ vars.companyRedressSchemeLinkUrl }}
              </nuxt-link>
            </p>
            <NuxtImg
              v-for="(item, index) in redress"
              :key="`redress-index-${index}`"
              :src="item.photo"
              alt="property-mark"
              class="tw-max-w-24"
            />
          </div>
        </div>
      </div>
      <small class="tw-flex-auto tw-opacity-60">
        Powered by
        <b>Neuron</b>
        |
        <b>Iceberg Digital</b>
      </small>
    </div>
    <div
      class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-6 tw-bg-[#aead66] tw-px-16 tw-py-6 tw-text-center md:tw-flex-row md:tw-justify-between 2xl:tw-px-48"
    >
      <div class="tw-flex tw-flex-row tw-gap-4">
        <nuxt-link
          v-if="vars.facebookUrl"
          :to="vars.facebookUrl"
          target="_blank"
          class="n-link tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M9.33329 8.99967H11L11.6666 6.33301H9.33329V4.99967C9.33329 4.31301 9.33329 3.66634 10.6666 3.66634H11.6666V1.42634C11.4493 1.39767 10.6286 1.33301 9.76196 1.33301C7.95196 1.33301 6.66663 2.43767 6.66663 4.46634V6.33301H4.66663V8.99967H6.66663V14.6663H9.33329V8.99967Z"
              fill="var(--c-secondary)"
            />
          </svg>
        </nuxt-link>
        <nuxt-link
          v-if="vars.instagramUrl"
          :to="vars.instagramUrl"
          target="_blank"
          class="n-link tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5.20004 1.33301H10.8C12.9334 1.33301 14.6667 3.06634 14.6667 5.19967V10.7997C14.6667 11.8252 14.2593 12.8087 13.5342 13.5338C12.809 14.259 11.8255 14.6663 10.8 14.6663H5.20004C3.06671 14.6663 1.33337 12.933 1.33337 10.7997V5.19967C1.33337 4.17417 1.74075 3.19067 2.46589 2.46553C3.19104 1.74039 4.17454 1.33301 5.20004 1.33301ZM5.06671 2.66634C4.43019 2.66634 3.81974 2.9192 3.36965 3.36928C2.91956 3.81937 2.66671 4.42982 2.66671 5.06634V10.933C2.66671 12.2597 3.74004 13.333 5.06671 13.333H10.9334C11.5699 13.333 12.1803 13.0802 12.6304 12.6301C13.0805 12.18 13.3334 11.5695 13.3334 10.933V5.06634C13.3334 3.73967 12.26 2.66634 10.9334 2.66634H5.06671ZM11.5 3.66634C11.7211 3.66634 11.933 3.75414 12.0893 3.91042C12.2456 4.0667 12.3334 4.27866 12.3334 4.49967C12.3334 4.72069 12.2456 4.93265 12.0893 5.08893C11.933 5.24521 11.7211 5.33301 11.5 5.33301C11.279 5.33301 11.0671 5.24521 10.9108 5.08893C10.7545 4.93265 10.6667 4.72069 10.6667 4.49967C10.6667 4.27866 10.7545 4.0667 10.9108 3.91042C11.0671 3.75414 11.279 3.66634 11.5 3.66634ZM8.00004 4.66634C8.8841 4.66634 9.73194 5.01753 10.3571 5.64265C10.9822 6.26777 11.3334 7.11562 11.3334 7.99967C11.3334 8.88373 10.9822 9.73158 10.3571 10.3567C9.73194 10.9818 8.8841 11.333 8.00004 11.333C7.11599 11.333 6.26814 10.9818 5.64302 10.3567C5.0179 9.73158 4.66671 8.88373 4.66671 7.99967C4.66671 7.11562 5.0179 6.26777 5.64302 5.64265C6.26814 5.01753 7.11599 4.66634 8.00004 4.66634ZM8.00004 5.99967C7.46961 5.99967 6.9609 6.21039 6.58583 6.58546C6.21075 6.96053 6.00004 7.46924 6.00004 7.99967C6.00004 8.53011 6.21075 9.03882 6.58583 9.41389C6.9609 9.78896 7.46961 9.99967 8.00004 9.99967C8.53047 9.99967 9.03918 9.78896 9.41425 9.41389C9.78933 9.03882 10 8.53011 10 7.99967C10 7.46924 9.78933 6.96053 9.41425 6.58546C9.03918 6.21039 8.53047 5.99967 8.00004 5.99967Z"
              fill="var(--c-secondary)"
            />
          </svg>
        </nuxt-link>
        <nuxt-link
          v-if="vars.linkedinUrl"
          :to="vars.linkedinUrl"
          target="_blank"
          class="n-link tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4.62663 3.33302C4.62645 3.68665 4.48581 4.02571 4.23563 4.27564C3.98546 4.52556 3.64625 4.66587 3.29263 4.66569C2.93901 4.66551 2.59994 4.52487 2.35001 4.27469C2.10009 4.02452 1.95978 3.68531 1.95996 3.33169C1.96014 2.97807 2.10078 2.639 2.35096 2.38908C2.60113 2.13915 2.94034 1.99885 3.29396 1.99902C3.64758 1.9992 3.98665 2.13985 4.23658 2.39002C4.4865 2.64019 4.6268 2.9794 4.62663 3.33302ZM4.66663 5.65302H1.99996V13.9997H4.66663V5.65302ZM8.87996 5.65302H6.22663V13.9997H8.85329V9.61969C8.85329 7.17969 12.0333 6.95302 12.0333 9.61969V13.9997H14.6666V8.71302C14.6666 4.59969 9.95996 4.75302 8.85329 6.77302L8.87996 5.65302Z"
              fill="#11273E"
            />
          </svg>
        </nuxt-link>
        <nuxt-link
          v-if="vars.youtubeUrl"
          :to="vars.youtubeUrl"
          target="_blank"
          class="n-link tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_1287_50)">
              <path
                d="M8.00004 2.66699C8.57004 2.66699 9.15471 2.68166 9.72137 2.70566L10.3907 2.73766L11.0314 2.77566L11.6314 2.81633L12.1794 2.85899C12.7741 2.90451 13.3337 3.15829 13.7597 3.57569C14.1858 3.99308 14.451 4.54735 14.5087 5.14099L14.5354 5.42433L14.5854 6.03099C14.632 6.65966 14.6667 7.34499 14.6667 8.00033C14.6667 8.65566 14.632 9.34099 14.5854 9.96966L14.5354 10.5763C14.5267 10.6737 14.518 10.7677 14.5087 10.8597C14.451 11.4534 14.1857 12.0078 13.7595 12.4252C13.3333 12.8426 12.7735 13.0963 12.1787 13.1417L11.632 13.1837L11.032 13.225L10.3907 13.263L9.72137 13.295C9.14793 13.3199 8.57403 13.3328 8.00004 13.3337C7.42606 13.3328 6.85215 13.3199 6.27871 13.295L5.60937 13.263L4.96871 13.225L4.36871 13.1837L3.82071 13.1417C3.22601 13.0961 2.66641 12.8424 2.24036 12.425C1.8143 12.0076 1.54909 11.4533 1.49137 10.8597L1.46471 10.5763L1.41471 9.96966C1.36373 9.31442 1.3366 8.65754 1.33337 8.00033C1.33337 7.34499 1.36804 6.65966 1.41471 6.03099L1.46471 5.42433C1.47337 5.32699 1.48204 5.23299 1.49137 5.14099C1.54907 4.54746 1.81419 3.99327 2.2401 3.57589C2.66602 3.15851 3.22546 2.90466 3.82004 2.85899L4.36737 2.81633L4.96737 2.77566L5.60871 2.73766L6.27804 2.70566C6.85171 2.68075 7.42583 2.66786 8.00004 2.66699ZM8.00004 4.00033C7.45004 4.00033 6.88404 4.01499 6.33337 4.03766L5.68137 4.06899L5.05537 4.10566L4.46737 4.14566L3.92871 4.18766C3.64559 4.20775 3.37874 4.32742 3.17544 4.52548C2.97214 4.72353 2.84552 4.98717 2.81804 5.26966C2.74004 6.07566 2.66671 7.07899 2.66671 8.00033C2.66671 8.92166 2.74004 9.92499 2.81804 10.731C2.87471 11.3123 3.33604 11.7643 3.92871 11.813L4.46737 11.8543L5.05537 11.8943L5.68137 11.9317L6.33337 11.963C6.88404 11.9857 7.45004 12.0003 8.00004 12.0003C8.55004 12.0003 9.11604 11.9857 9.66671 11.963L10.3187 11.9317L10.9447 11.895L11.5327 11.855L12.0714 11.813C12.3545 11.7929 12.6213 11.6732 12.8246 11.4752C13.0279 11.2771 13.1546 11.0135 13.182 10.731C13.26 9.92499 13.3334 8.92166 13.3334 8.00033C13.3334 7.07899 13.26 6.07566 13.182 5.26966C13.1546 4.98717 13.0279 4.72353 12.8246 4.52548C12.6213 4.32742 12.3545 4.20775 12.0714 4.18766L11.5327 4.14633L10.9447 4.10633L10.3187 4.06899L9.66671 4.03766C9.11147 4.01377 8.55579 4.00132 8.00004 4.00033ZM6.66671 6.38366C6.66666 6.3185 6.68254 6.25432 6.71295 6.19669C6.74336 6.13907 6.78739 6.08975 6.84121 6.05302C6.89503 6.01628 6.957 5.99325 7.02175 5.98593C7.08649 5.97861 7.15204 5.98721 7.21271 6.01099L7.26671 6.03766L10.0667 7.65366C10.1225 7.68583 10.1696 7.73098 10.2042 7.78528C10.2388 7.83958 10.2597 7.90143 10.2653 7.96556C10.2709 8.02969 10.2609 8.09422 10.2362 8.15366C10.2114 8.2131 10.1728 8.2657 10.1234 8.30699L10.0667 8.34699L7.26671 9.96366C7.21028 9.99633 7.14672 10.0147 7.08157 10.0172C7.01642 10.0197 6.95164 10.0062 6.89288 9.97798C6.83412 9.94972 6.78316 9.90752 6.74444 9.85506C6.70571 9.8026 6.6804 9.74147 6.67071 9.67699L6.66671 9.61699V6.38366Z"
                fill="var(--c-secondary)"
              />
            </g>
            <defs>
              <clipPath id="clip0_1287_50">
                <rect width="16" height="16" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </nuxt-link>
      </div>
      <p class="tw-whitespace-nowrap tw-text-sm tw-text-white">
        © {{ new Date().getFullYear() }} Allen Residential. All rights reserved.
      </p>
    </div>
  </footer>

  <footer
    v-if="variants.v2 === variant"
    class="tw-relative tw-flex tw-flex-col tw-text-start !tw-text-white"
    :style="`background-color: var(--c-${vars.backgroundColor});`"
  >
    <span
      v-if="vars.watermarkImage"
      class="watermark tw-pointer-events-none tw-absolute -tw-left-40 tw-bottom-0 tw-z-0"
    >
      <img :src="vars.watermarkImage" alt="watermark" class="tw-w-[900px] tw-opacity-20" />
    </span>
    <div
      class="tw-flex tw-flex-col tw-justify-center tw-gap-14 tw-p-8 tw-pb-8 sm:tw-p-16 md:tw-flex-row md:tw-flex-wrap 2xl:tw-gap-6 2xl:tw-pt-28"
    >
      <div class="tw-flex tw-flex-col tw-items-center tw-gap-12 lg:tw-items-start xl:tw-flex-row xl:tw-gap-36">
        <div
          class="tw-flex tw-max-w-[500px] tw-flex-[30%] tw-flex-col tw-items-center tw-gap-5 tw-text-center lg:tw-items-start lg:tw-text-start"
        >
          <NuxtImg :src="company.logo" class="tw-max-w-60" />
          <small class="tw-opacity-70">
            {{ vars.descriptionText }}
          </small>
          <div class="tw-flex tw-flex-row tw-gap-4">
            <nuxt-link
              v-if="vars.facebookUrl"
              :to="vars.facebookUrl"
              target="_blank"
              class="n-link group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-secondary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9.33317 9.00016H10.9998L11.6665 6.3335H9.33317V5.00016C9.33317 4.3135 9.33317 3.66683 10.6665 3.66683H11.6665V1.42683C11.4492 1.39816 10.6285 1.3335 9.76184 1.3335C7.95184 1.3335 6.6665 2.43816 6.6665 4.46683V6.3335H4.6665V9.00016H6.6665V14.6668H9.33317V9.00016Z"
                  fill="var(--c-secondary)"
                />
              </svg>
            </nuxt-link>
            <nuxt-link
              v-if="vars.instagramUrl"
              :to="vars.instagramUrl"
              target="_blank"
              class="n-link group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-secondary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M5.20016 1.3335H10.8002C12.9335 1.3335 14.6668 3.06683 14.6668 5.20016V10.8002C14.6668 11.8257 14.2594 12.8092 13.5343 13.5343C12.8092 14.2594 11.8257 14.6668 10.8002 14.6668H5.20016C3.06683 14.6668 1.3335 12.9335 1.3335 10.8002V5.20016C1.3335 4.17466 1.74088 3.19116 2.46602 2.46602C3.19116 1.74088 4.17466 1.3335 5.20016 1.3335ZM5.06683 2.66683C4.43031 2.66683 3.81986 2.91969 3.36977 3.36977C2.91969 3.81986 2.66683 4.43031 2.66683 5.06683V10.9335C2.66683 12.2602 3.74016 13.3335 5.06683 13.3335H10.9335C11.57 13.3335 12.1805 13.0806 12.6306 12.6306C13.0806 12.1805 13.3335 11.57 13.3335 10.9335V5.06683C13.3335 3.74016 12.2602 2.66683 10.9335 2.66683H5.06683ZM11.5002 3.66683C11.7212 3.66683 11.9331 3.75463 12.0894 3.91091C12.2457 4.06719 12.3335 4.27915 12.3335 4.50016C12.3335 4.72118 12.2457 4.93314 12.0894 5.08942C11.9331 5.2457 11.7212 5.3335 11.5002 5.3335C11.2791 5.3335 11.0672 5.2457 10.9109 5.08942C10.7546 4.93314 10.6668 4.72118 10.6668 4.50016C10.6668 4.27915 10.7546 4.06719 10.9109 3.91091C11.0672 3.75463 11.2791 3.66683 11.5002 3.66683ZM8.00016 4.66683C8.88422 4.66683 9.73206 5.01802 10.3572 5.64314C10.9823 6.26826 11.3335 7.11611 11.3335 8.00016C11.3335 8.88422 10.9823 9.73206 10.3572 10.3572C9.73206 10.9823 8.88422 11.3335 8.00016 11.3335C7.11611 11.3335 6.26826 10.9823 5.64314 10.3572C5.01802 9.73206 4.66683 8.88422 4.66683 8.00016C4.66683 7.11611 5.01802 6.26826 5.64314 5.64314C6.26826 5.01802 7.11611 4.66683 8.00016 4.66683ZM8.00016 6.00016C7.46973 6.00016 6.96102 6.21088 6.58595 6.58595C6.21088 6.96102 6.00016 7.46973 6.00016 8.00016C6.00016 8.5306 6.21088 9.0393 6.58595 9.41438C6.96102 9.78945 7.46973 10.0002 8.00016 10.0002C8.5306 10.0002 9.0393 9.78945 9.41438 9.41438C9.78945 9.0393 10.0002 8.5306 10.0002 8.00016C10.0002 7.46973 9.78945 6.96102 9.41438 6.58595C9.0393 6.21088 8.5306 6.00016 8.00016 6.00016Z"
                  fill="var(--c-secondary)"
                />
              </svg>
            </nuxt-link>
            <nuxt-link
              v-if="vars.linkedinUrl"
              :to="vars.linkedinUrl"
              target="_blank"
              class="n-link tw-group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-secondary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.62663 3.33351C4.62645 3.68713 4.48581 4.0262 4.23563 4.27613C3.98546 4.52605 3.64625 4.66636 3.29263 4.66618C2.93901 4.666 2.59994 4.52536 2.35001 4.27518C2.10009 4.02501 1.95978 3.6858 1.95996 3.33218C1.96014 2.97856 2.10078 2.63949 2.35096 2.38956C2.60113 2.13964 2.94034 1.99934 3.29396 1.99951C3.64758 1.99969 3.98665 2.14033 4.23658 2.39051C4.4865 2.64068 4.6268 2.97989 4.62663 3.33351ZM4.66663 5.65351H1.99996V14.0002H4.66663V5.65351ZM8.87996 5.65351H6.22663V14.0002H8.85329V9.62018C8.85329 7.18018 12.0333 6.95351 12.0333 9.62018V14.0002H14.6666V8.71351C14.6666 4.60018 9.95996 4.75351 8.85329 6.77351L8.87996 5.65351Z"
                  fill="var(--c-secondary)"
                />
              </svg>
            </nuxt-link>
            <nuxt-link
              v-if="vars.youtubeUrl"
              :to="vars.youtubeUrl"
              target="_blank"
              class="n-link group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-secondary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_23_2087)">
                  <path
                    d="M8.00016 2.6665C8.57016 2.6665 9.15483 2.68117 9.7215 2.70517L10.3908 2.73717L11.0315 2.77517L11.6315 2.81584L12.1795 2.8585C12.7742 2.90402 13.3338 3.1578 13.7598 3.5752C14.1859 3.99259 14.4511 4.54686 14.5088 5.1405L14.5355 5.42384L14.5855 6.0305C14.6322 6.65917 14.6668 7.3445 14.6668 7.99984C14.6668 8.65517 14.6322 9.3405 14.5855 9.96917L14.5355 10.5758C14.5268 10.6732 14.5182 10.7672 14.5088 10.8592C14.4511 11.4529 14.1858 12.0073 13.7596 12.4247C13.3334 12.8421 12.7736 13.0958 12.1788 13.1412L11.6322 13.1832L11.0322 13.2245L10.3908 13.2625L9.7215 13.2945C9.14805 13.3194 8.57415 13.3323 8.00016 13.3332C7.42618 13.3323 6.85227 13.3194 6.27883 13.2945L5.6095 13.2625L4.96883 13.2245L4.36883 13.1832L3.82083 13.1412C3.22613 13.0957 2.66653 12.8419 2.24048 12.4245C1.81443 12.0071 1.54921 11.4528 1.4915 10.8592L1.46483 10.5758L1.41483 9.96917C1.36385 9.31393 1.33673 8.65705 1.3335 7.99984C1.3335 7.3445 1.36816 6.65917 1.41483 6.0305L1.46483 5.42384C1.4735 5.3265 1.48216 5.2325 1.4915 5.1405C1.54919 4.54697 1.81431 3.99278 2.24022 3.5754C2.66614 3.15802 3.22558 2.90417 3.82016 2.8585L4.3675 2.81584L4.9675 2.77517L5.60883 2.73717L6.27816 2.70517C6.85183 2.68026 7.42596 2.66737 8.00016 2.6665ZM8.00016 3.99984C7.45016 3.99984 6.88416 4.0145 6.3335 4.03717L5.6815 4.0685L5.0555 4.10517L4.4675 4.14517L3.92883 4.18717C3.64572 4.20726 3.37886 4.32693 3.17556 4.52499C2.97226 4.72304 2.84564 4.98668 2.81816 5.26917C2.74016 6.07517 2.66683 7.0785 2.66683 7.99984C2.66683 8.92117 2.74016 9.9245 2.81816 10.7305C2.87483 11.3118 3.33616 11.7638 3.92883 11.8125L4.4675 11.8538L5.0555 11.8938L5.6815 11.9312L6.3335 11.9625C6.88416 11.9852 7.45016 11.9998 8.00016 11.9998C8.55016 11.9998 9.11616 11.9852 9.66683 11.9625L10.3188 11.9312L10.9448 11.8945L11.5328 11.8545L12.0715 11.8125C12.3546 11.7924 12.6215 11.6727 12.8248 11.4747C13.0281 11.2766 13.1547 11.013 13.1822 10.7305C13.2602 9.9245 13.3335 8.92117 13.3335 7.99984C13.3335 7.0785 13.2602 6.07517 13.1822 5.26917C13.1547 4.98668 13.0281 4.72304 12.8248 4.52499C12.6215 4.32693 12.3546 4.20726 12.0715 4.18717L11.5328 4.14584L10.9448 4.10584L10.3188 4.0685L9.66683 4.03717C9.11159 4.01328 8.55592 4.00083 8.00016 3.99984ZM6.66683 6.38317C6.66679 6.31801 6.68266 6.25383 6.71307 6.19621C6.74349 6.13858 6.78751 6.08926 6.84133 6.05253C6.89515 6.01579 6.95712 5.99277 7.02187 5.98544C7.08661 5.97812 7.15217 5.98672 7.21283 6.0105L7.26683 6.03717L10.0668 7.65317C10.1226 7.68534 10.1698 7.73049 10.2043 7.78479C10.2389 7.83909 10.2599 7.90094 10.2654 7.96507C10.271 8.0292 10.261 8.09373 10.2363 8.15317C10.2116 8.21261 10.1729 8.26521 10.1235 8.3065L10.0668 8.3465L7.26683 9.96317C7.2104 9.99584 7.14685 10.0142 7.08169 10.0167C7.01654 10.0192 6.95176 10.0058 6.893 9.97749C6.83424 9.94923 6.78328 9.90703 6.74456 9.85457C6.70584 9.80211 6.68053 9.74098 6.67083 9.6765L6.66683 9.6165V6.38317Z"
                    fill="var(--c-secondary)"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_23_2087">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </nuxt-link>
            <nuxt-link
              v-if="vars.tiktokUrl"
              :to="vars.tiktokUrl"
              target="_blank"
              class="n-link group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-secondary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M11.0668 3.88C10.6111 3.35975 10.3599 2.69162 10.3601 2H8.3001V10.2667C8.28422 10.714 8.09535 11.1377 7.77328 11.4486C7.45121 11.7595 7.02107 11.9333 6.57344 11.9333C5.62677 11.9333 4.8401 11.16 4.8401 10.2C4.8401 9.05333 5.94677 8.19333 7.08677 8.54667V6.44C4.78677 6.13333 2.77344 7.92 2.77344 10.2C2.77344 12.42 4.61344 14 6.56677 14C8.6601 14 10.3601 12.3 10.3601 10.2V6.00667C11.1954 6.60657 12.1983 6.92843 13.2268 6.92667V4.86667C13.2268 4.86667 11.9734 4.92667 11.0668 3.88Z"
                  fill="var(--c-secondary)"
                />
              </svg>
            </nuxt-link>
          </div>
        </div>
        <div
          class="tw-flex tw-flex-row tw-flex-wrap tw-justify-center tw-gap-14 md:tw-flex-row lg:tw-flex-nowrap lg:tw-justify-start xl:tw-gap-24"
        >
          <div class="tw-flex tw-flex-col-reverse tw-gap-12 sm:tw-flex-row-reverse xl:tw-flex-row xl:tw-gap-24">
            <div
              class="tw-flex tw-min-w-[142px] tw-flex-col tw-items-center tw-gap-5 tw-text-center lg:tw-items-start lg:tw-text-start"
            >
              <h5 class="tw-break-normal">
                {{ vars.columnTitle1Text }}
              </h5>
              <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 lg:tw-items-start">
                <nuxt-link
                  v-for="(column1, index) in columns1"
                  :key="index"
                  :to="column1.link"
                  :external="true"
                  class="n-link tw-flex tw-flex-row tw-items-center tw-gap-2 tw-duration-300 hover:tw-text-[var(--c-secondary)]"
                  ><span v-if="column1.icon"><NuxtImg :src="column1.icon" class="tw-w-4 tw-min-w-4" /></span
                  ><small class="tw-opacity-70">{{ column1.item }}</small></nuxt-link
                >
              </div>
            </div>
            <div
              class="tw-flex tw-min-w-[140px] tw-flex-[1_1_50%] tw-flex-col tw-items-center tw-gap-5 tw-text-center lg:tw-items-start lg:tw-text-start"
            >
              <h5 class="tw-break-normal">
                {{ vars.columnTitle2Text }}
              </h5>
              <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 lg:tw-items-start">
                <nuxt-link
                  v-for="(column2, index) in columns2"
                  :key="`column2-${index}`"
                  :to="column2.link"
                  :external="true"
                  class="n-link tw-flex tw-flex-row tw-items-center tw-gap-2 tw-duration-300 hover:tw-text-[var(--c-secondary)]"
                  ><span v-if="column2.icon"><NuxtImg :src="column2.icon" class="tw-w-4 tw-min-w-4" /></span>
                  <small class="tw-opacity-70">{{ column2.item }}</small></nuxt-link
                >
              </div>
            </div>
          </div>
          <div
            class="tw-flex tw-max-w-[500px] tw-flex-col tw-items-center tw-gap-5 tw-text-center lg:tw-items-start lg:tw-text-start"
          >
            <small class="tw-opacity-70">{{ vars.companyTradingAsText }}</small>
            <small class="tw-opacity-70">{{ vars.companyText }}</small>
            <small class="tw-opacity-70">Company Number: {{ vars.companyNumber }}</small>
            <small class="tw-opacity-70">Registered Address: {{ vars.companyAddressText }}</small>
            <small class="tw-opacity-70"> Redress Scheme: {{ vars.companyRedressSchemeText }} </small>
            <small class="tw-opacity-70">
              <nuxt-link :external="true" :to="vars.companyRedressSchemeLinkUrl" class="n-link" target="_blank">
                {{ vars.companyRedressSchemeLinkUrl }}
              </nuxt-link></small
            >
            <a v-for="(logo, index) in redress" :key="`redressLogos-${index}`" class="n-link">
              <img style="max-height: 42px" :title="logo.title" :alt="logo.title" :src="logo.photo" />
            </a>
          </div>
        </div>
      </div>
    </div>
    <div
      class="tw-z-10 tw-flex tw-flex-wrap tw-items-start tw-justify-around tw-gap-6 tw-bg-[var(--c-secondary)] tw-p-6 tw-text-center md:tw-flex-nowrap"
    >
      <small class="tw-whitespace-nowrap tw-text-start"
        >Powered by
        <span class="tw-font-bold">
          <a
            href="https://www.iceberg-digital.co.uk/neuron/home"
            target="_blank"
            class="n-link"
            style="font-family: var(--f-secondary) !important"
            >Neuron
          </a> </span
        >|
        <span class="tw-font-bold">
          <a
            href="https://www.iceberg-digital.co.uk/"
            target="_blank"
            class="n-link"
            style="font-family: var(--f-secondary) !important"
            >Iceberg Digital
          </a>
        </span>
      </small>
      <small class="tw-whitespace-nowrap tw-text-sm"
        >© {{ new Date().getFullYear() }} {{ company.name }} All rights reserved.</small
      >
    </div>
  </footer>

  <footer
    v-if="variants.v3 === variant"
    class="tw-relative tw-flex tw-flex-col tw-text-start !tw-text-white"
    :style="`background-color: var(--c-${vars.backgroundColor});`"
  >
    <span
      v-if="vars.watermarkImage"
      class="watermark tw-pointer-events-none tw-absolute tw-left-[600px] tw-bottom-20 tw-z-0"
    >
      <img :src="vars.watermarkImage" alt="watermark" class="tw-w-[300px] tw-opacity-100" />
    </span>
    <div
      class="tw-flex tw-flex-col tw-justify-center tw-gap-14 tw-p-8 tw-pb-8 sm:tw-p-16 md:tw-flex-row md:tw-flex-wrap 2xl:tw-gap-6 2xl:tw-pt-28"
    >
      <div class="tw-flex tw-flex-col tw-items-center tw-gap-12 lg:tw-items-start xl:tw-flex-row xl:tw-gap-36">
        <div
          class="tw-flex tw-max-w-[500px] tw-flex-[30%] tw-flex-col tw-items-center tw-gap-5 tw-text-center lg:tw-items-start lg:tw-text-start"
        >
          <NuxtImg :src="company.logo" class="tw-max-w-60" />
         <small style="font-weight:300 !important;" class="tw-opacity-70 tw-max-w-80">
  {{ vars.descriptionText }}
</small>

          <div class="tw-flex tw-flex-row tw-gap-4">
            <nuxt-link
              v-if="vars.facebookUrl"
              :to="vars.facebookUrl"
              target="_blank"
              class="n-link group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-primary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9.33317 9.00016H10.9998L11.6665 6.3335H9.33317V5.00016C9.33317 4.3135 9.33317 3.66683 10.6665 3.66683H11.6665V1.42683C11.4492 1.39816 10.6285 1.3335 9.76184 1.3335C7.95184 1.3335 6.6665 2.43816 6.6665 4.46683V6.3335H4.6665V9.00016H6.6665V14.6668H9.33317V9.00016Z"
                  fill="var(--c-primary)"
                />
              </svg>
            </nuxt-link>
            <nuxt-link
              v-if="vars.instagramUrl"
              :to="vars.instagramUrl"
              target="_blank"
              class="n-link group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-primary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M5.20016 1.3335H10.8002C12.9335 1.3335 14.6668 3.06683 14.6668 5.20016V10.8002C14.6668 11.8257 14.2594 12.8092 13.5343 13.5343C12.8092 14.2594 11.8257 14.6668 10.8002 14.6668H5.20016C3.06683 14.6668 1.3335 12.9335 1.3335 10.8002V5.20016C1.3335 4.17466 1.74088 3.19116 2.46602 2.46602C3.19116 1.74088 4.17466 1.3335 5.20016 1.3335ZM5.06683 2.66683C4.43031 2.66683 3.81986 2.91969 3.36977 3.36977C2.91969 3.81986 2.66683 4.43031 2.66683 5.06683V10.9335C2.66683 12.2602 3.74016 13.3335 5.06683 13.3335H10.9335C11.57 13.3335 12.1805 13.0806 12.6306 12.6306C13.0806 12.1805 13.3335 11.57 13.3335 10.9335V5.06683C13.3335 3.74016 12.2602 2.66683 10.9335 2.66683H5.06683ZM11.5002 3.66683C11.7212 3.66683 11.9331 3.75463 12.0894 3.91091C12.2457 4.06719 12.3335 4.27915 12.3335 4.50016C12.3335 4.72118 12.2457 4.93314 12.0894 5.08942C11.9331 5.2457 11.7212 5.3335 11.5002 5.3335C11.2791 5.3335 11.0672 5.2457 10.9109 5.08942C10.7546 4.93314 10.6668 4.72118 10.6668 4.50016C10.6668 4.27915 10.7546 4.06719 10.9109 3.91091C11.0672 3.75463 11.2791 3.66683 11.5002 3.66683ZM8.00016 4.66683C8.88422 4.66683 9.73206 5.01802 10.3572 5.64314C10.9823 6.26826 11.3335 7.11611 11.3335 8.00016C11.3335 8.88422 10.9823 9.73206 10.3572 10.3572C9.73206 10.9823 8.88422 11.3335 8.00016 11.3335C7.11611 11.3335 6.26826 10.9823 5.64314 10.3572C5.01802 9.73206 4.66683 8.88422 4.66683 8.00016C4.66683 7.11611 5.01802 6.26826 5.64314 5.64314C6.26826 5.01802 7.11611 4.66683 8.00016 4.66683ZM8.00016 6.00016C7.46973 6.00016 6.96102 6.21088 6.58595 6.58595C6.21088 6.96102 6.00016 7.46973 6.00016 8.00016C6.00016 8.5306 6.21088 9.0393 6.58595 9.41438C6.96102 9.78945 7.46973 10.0002 8.00016 10.0002C8.5306 10.0002 9.0393 9.78945 9.41438 9.41438C9.78945 9.0393 10.0002 8.5306 10.0002 8.00016C10.0002 7.46973 9.78945 6.96102 9.41438 6.58595C9.0393 6.21088 8.5306 6.00016 8.00016 6.00016Z"
                  fill="var(--c-primary)"
                />
              </svg>
            </nuxt-link>
            <nuxt-link
              v-if="vars.linkedinUrl"
              :to="vars.linkedinUrl"
              target="_blank"
              class="n-link tw-group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-primary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.62663 3.33351C4.62645 3.68713 4.48581 4.0262 4.23563 4.27613C3.98546 4.52605 3.64625 4.66636 3.29263 4.66618C2.93901 4.666 2.59994 4.52536 2.35001 4.27518C2.10009 4.02501 1.95978 3.6858 1.95996 3.33218C1.96014 2.97856 2.10078 2.63949 2.35096 2.38956C2.60113 2.13964 2.94034 1.99934 3.29396 1.99951C3.64758 1.99969 3.98665 2.14033 4.23658 2.39051C4.4865 2.64068 4.6268 2.97989 4.62663 3.33351ZM4.66663 5.65351H1.99996V14.0002H4.66663V5.65351ZM8.87996 5.65351H6.22663V14.0002H8.85329V9.62018C8.85329 7.18018 12.0333 6.95351 12.0333 9.62018V14.0002H14.6666V8.71351C14.6666 4.60018 9.95996 4.75351 8.85329 6.77351L8.87996 5.65351Z"
                  fill="var(--c-primary)"
                />
              </svg>
            </nuxt-link>
            <nuxt-link
              v-if="vars.youtubeUrl"
              :to="vars.youtubeUrl"
              target="_blank"
              class="n-link group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-primary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_23_2087)">
                  <path
                    d="M8.00016 2.6665C8.57016 2.6665 9.15483 2.68117 9.7215 2.70517L10.3908 2.73717L11.0315 2.77517L11.6315 2.81584L12.1795 2.8585C12.7742 2.90402 13.3338 3.1578 13.7598 3.5752C14.1859 3.99259 14.4511 4.54686 14.5088 5.1405L14.5355 5.42384L14.5855 6.0305C14.6322 6.65917 14.6668 7.3445 14.6668 7.99984C14.6668 8.65517 14.6322 9.3405 14.5855 9.96917L14.5355 10.5758C14.5268 10.6732 14.5182 10.7672 14.5088 10.8592C14.4511 11.4529 14.1858 12.0073 13.7596 12.4247C13.3334 12.8421 12.7736 13.0958 12.1788 13.1412L11.6322 13.1832L11.0322 13.2245L10.3908 13.2625L9.7215 13.2945C9.14805 13.3194 8.57415 13.3323 8.00016 13.3332C7.42618 13.3323 6.85227 13.3194 6.27883 13.2945L5.6095 13.2625L4.96883 13.2245L4.36883 13.1832L3.82083 13.1412C3.22613 13.0957 2.66653 12.8419 2.24048 12.4245C1.81443 12.0071 1.54921 11.4528 1.4915 10.8592L1.46483 10.5758L1.41483 9.96917C1.36385 9.31393 1.33673 8.65705 1.3335 7.99984C1.3335 7.3445 1.36816 6.65917 1.41483 6.0305L1.46483 5.42384C1.4735 5.3265 1.48216 5.2325 1.4915 5.1405C1.54919 4.54697 1.81431 3.99278 2.24022 3.5754C2.66614 3.15802 3.22558 2.90417 3.82016 2.8585L4.3675 2.81584L4.9675 2.77517L5.60883 2.73717L6.27816 2.70517C6.85183 2.68026 7.42596 2.66737 8.00016 2.6665ZM8.00016 3.99984C7.45016 3.99984 6.88416 4.0145 6.3335 4.03717L5.6815 4.0685L5.0555 4.10517L4.4675 4.14517L3.92883 4.18717C3.64572 4.20726 3.37886 4.32693 3.17556 4.52499C2.97226 4.72304 2.84564 4.98668 2.81816 5.26917C2.74016 6.07517 2.66683 7.0785 2.66683 7.99984C2.66683 8.92117 2.74016 9.9245 2.81816 10.7305C2.87483 11.3118 3.33616 11.7638 3.92883 11.8125L4.4675 11.8538L5.0555 11.8938L5.6815 11.9312L6.3335 11.9625C6.88416 11.9852 7.45016 11.9998 8.00016 11.9998C8.55016 11.9998 9.11616 11.9852 9.66683 11.9625L10.3188 11.9312L10.9448 11.8945L11.5328 11.8545L12.0715 11.8125C12.3546 11.7924 12.6215 11.6727 12.8248 11.4747C13.0281 11.2766 13.1547 11.013 13.1822 10.7305C13.2602 9.9245 13.3335 8.92117 13.3335 7.99984C13.3335 7.0785 13.2602 6.07517 13.1822 5.26917C13.1547 4.98668 13.0281 4.72304 12.8248 4.52499C12.6215 4.32693 12.3546 4.20726 12.0715 4.18717L11.5328 4.14584L10.9448 4.10584L10.3188 4.0685L9.66683 4.03717C9.11159 4.01328 8.55592 4.00083 8.00016 3.99984ZM6.66683 6.38317C6.66679 6.31801 6.68266 6.25383 6.71307 6.19621C6.74349 6.13858 6.78751 6.08926 6.84133 6.05253C6.89515 6.01579 6.95712 5.99277 7.02187 5.98544C7.08661 5.97812 7.15217 5.98672 7.21283 6.0105L7.26683 6.03717L10.0668 7.65317C10.1226 7.68534 10.1698 7.73049 10.2043 7.78479C10.2389 7.83909 10.2599 7.90094 10.2654 7.96507C10.271 8.0292 10.261 8.09373 10.2363 8.15317C10.2116 8.21261 10.1729 8.26521 10.1235 8.3065L10.0668 8.3465L7.26683 9.96317C7.2104 9.99584 7.14685 10.0142 7.08169 10.0167C7.01654 10.0192 6.95176 10.0058 6.893 9.97749C6.83424 9.94923 6.78328 9.90703 6.74456 9.85457C6.70584 9.80211 6.68053 9.74098 6.67083 9.6765L6.66683 9.6165V6.38317Z"
                    fill="var(--c-primary)"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_23_2087">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </nuxt-link>
            <nuxt-link
              v-if="vars.tiktokUrl"
              :to="vars.tiktokUrl"
              target="_blank"
              class="n-link group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-primary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M11.0668 3.88C10.6111 3.35975 10.3599 2.69162 10.3601 2H8.3001V10.2667C8.28422 10.714 8.09535 11.1377 7.77328 11.4486C7.45121 11.7595 7.02107 11.9333 6.57344 11.9333C5.62677 11.9333 4.8401 11.16 4.8401 10.2C4.8401 9.05333 5.94677 8.19333 7.08677 8.54667V6.44C4.78677 6.13333 2.77344 7.92 2.77344 10.2C2.77344 12.42 4.61344 14 6.56677 14C8.6601 14 10.3601 12.3 10.3601 10.2V6.00667C11.1954 6.60657 12.1983 6.92843 13.2268 6.92667V4.86667C13.2268 4.86667 11.9734 4.92667 11.0668 3.88Z"
                  fill="var(--c-primary)"
                />
              </svg>
            </nuxt-link>
          </div>
        </div>
        <div
          class="tw-flex tw-flex-row tw-flex-wrap tw-justify-center tw-gap-14 md:tw-flex-row lg:tw-flex-nowrap lg:tw-justify-start xl:tw-gap-24"
        >
          <div class="tw-flex tw-flex-col-reverse tw-gap-12 sm:tw-flex-row-reverse xl:tw-flex-row xl:tw-gap-24">
            <div
              class="tw-flex tw-min-w-[172px] tw-flex-col tw-items-center tw-gap-5 tw-text-center lg:tw-items-start lg:tw-text-start"
            >
              <h4 class="tw-break-normal">
                {{ vars.columnTitle1Text }}
              </h4>
              <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 lg:tw-items-start">
                <nuxt-link
                  v-for="(column1, index) in columns1"
                  :key="index"
                  :to="column1.link"
                  :external="true"
                  class="n-link tw-flex tw-flex-row tw-items-center tw-gap-2 tw-duration-300 hover:tw-text-[var(--c-primary)]"
                  ><span v-if="column1.icon"><NuxtImg :src="column1.icon" class="tw-w-4 tw-min-w-4" /></span
                  ><small class="tw-opacity-70" >{{ column1.item }}</small></nuxt-link
                >
              </div>
            </div>
            <div
              class="tw-flex tw-min-w-[140px] tw-flex-[1_1_50%] tw-flex-col tw-items-center tw-gap-5 tw-text-center lg:tw-items-start lg:tw-text-start"
            >
              <h4 class="tw-break-normal">
                {{ vars.columnTitle2Text }}
              </h4>
              <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 lg:tw-items-start">
                <nuxt-link
                  v-for="(column2, index) in columns2"
                  :key="`column2-${index}`"
                  :to="column2.link"
                  :external="true"
                  class="n-link tw-flex tw-flex-row tw-items-center tw-gap-2 tw-duration-300 hover:tw-text-[var(--c-primary)]"
                  ><span v-if="column2.icon"><NuxtImg :src="column2.icon" class="tw-w-4 tw-min-w-4" /></span>
                  <small class="tw-opacity-70 ">{{ column2.item }}</small></nuxt-link
                >
              </div>
            </div>
          </div>
          <div
            class="tw-flex tw-max-w-[500px] tw-flex-col tw-items-center tw-gap-5 tw-text-center lg:tw-items-start lg:tw-text-start"
          >
            <small style="font-weight:300 !important;" class="tw-opacity-70">{{ vars.companyTradingAsText }}</small>
            <small style="font-weight:300 !important;" class="tw-opacity-70">{{ vars.companyText }}</small>
            <small style="font-weight:300 !important;" class="tw-opacity-70">Company Number: {{ vars.companyNumber }}</small>
            <small style="font-weight:300 !important;" class="tw-opacity-70">Registered Address: {{ vars.companyAddressText }}</small>
            <small style="font-weight:300 !important;" class="tw-opacity-70"> Redress Scheme: {{ vars.companyRedressSchemeText }} </small>
            <small style="font-weight:300 !important;" class="tw-opacity-70">
              <nuxt-link :external="true" :to="vars.companyRedressSchemeLinkUrl" class="n-link" target="_blank">
                {{ vars.companyRedressSchemeLinkUrl }}
              </nuxt-link></small
            >
            <a v-for="(logo, index) in redress" :key="`redressLogos-${index}`" class="n-link">
              <img style="max-height: 42px" :title="logo.title" :alt="logo.title" :src="logo.photo" />
            </a>
          </div>
        </div>
      </div>
    </div>
    <div
      class="tw-z-10 tw-flex tw-flex-wrap tw-items-start tw-justify-around tw-gap-6 tw-bg-[var(--c-primary)] tw-p-6 tw-text-center md:tw-flex-nowrap"
    >
      <small style="font-weight:300 !important;" class="tw-whitespace-nowrap tw-text-start"
        >Powered by
        <span class="tw-font-bold">
          <a
            href="https://www.iceberg-digital.co.uk/neuron/home"
            target="_blank"
            class="n-link"
            style="font-family: var(--f-secondary) !important"
            >Neuron
          </a> </span
        >|
        <span class="tw-font-bold">
          <a
            href="https://www.iceberg-digital.co.uk/"
            target="_blank"
            class="n-link"
            style="font-family: var(--f-secondary) !important"
            >Iceberg Digital
          </a>
        </span>
      </small>
      <small style="font-weight:300 !important;" class="tw-whitespace-nowrap tw-text-sm"
        >© {{ new Date().getFullYear() }} {{ company.name }} All rights reserved.</small
      >
    </div>
  </footer>

  
  <footer
    v-if="variants.v4 === variant"
    class="tw-relative tw-flex tw-flex-col tw-text-start !tw-text-white"
    :style="`background-color: var(--c-${vars.backgroundColor});`"
  >
    <span
      v-if="vars.watermarkImage"
      class="watermark tw-pointer-events-none tw-absolute tw-left-[600px] tw-bottom-20 tw-z-0"
    >
      <img :src="vars.watermarkImage" alt="watermark" class="tw-w-[300px] tw-opacity-100" />
    </span>
    <div
      class="tw-flex tw-flex-col tw-justify-center tw-gap-14 tw-p-8 tw-pb-8 sm:tw-p-16 md:tw-flex-row md:tw-flex-wrap 2xl:tw-gap-6 2xl:tw-pt-28"
    >
      <div class="tw-flex tw-flex-col tw-items-center tw-gap-12 lg:tw-items-start xl:tw-flex-row xl:tw-gap-36">
        <div
          class="tw-flex tw-max-w-[500px] tw-flex-[30%] tw-flex-col tw-items-center tw-gap-5 tw-text-center lg:tw-items-start lg:tw-text-start"
        >
          <NuxtImg :src="company.logo" class="tw-max-w-60" />
         <small style="font-weight:300 !important;" class="tw-opacity-70 tw-max-w-80">
  {{ vars.descriptionText }}
</small>

          <div class="tw-flex tw-flex-row tw-gap-4">
            <nuxt-link
              v-if="vars.facebookUrl"
              :to="vars.facebookUrl"
              target="_blank"
              class="n-link group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-primary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9.33317 9.00016H10.9998L11.6665 6.3335H9.33317V5.00016C9.33317 4.3135 9.33317 3.66683 10.6665 3.66683H11.6665V1.42683C11.4492 1.39816 10.6285 1.3335 9.76184 1.3335C7.95184 1.3335 6.6665 2.43816 6.6665 4.46683V6.3335H4.6665V9.00016H6.6665V14.6668H9.33317V9.00016Z"
                  fill="var(--c-primary)"
                />
              </svg>
            </nuxt-link>
            <nuxt-link
              v-if="vars.instagramUrl"
              :to="vars.instagramUrl"
              target="_blank"
              class="n-link group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-primary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M5.20016 1.3335H10.8002C12.9335 1.3335 14.6668 3.06683 14.6668 5.20016V10.8002C14.6668 11.8257 14.2594 12.8092 13.5343 13.5343C12.8092 14.2594 11.8257 14.6668 10.8002 14.6668H5.20016C3.06683 14.6668 1.3335 12.9335 1.3335 10.8002V5.20016C1.3335 4.17466 1.74088 3.19116 2.46602 2.46602C3.19116 1.74088 4.17466 1.3335 5.20016 1.3335ZM5.06683 2.66683C4.43031 2.66683 3.81986 2.91969 3.36977 3.36977C2.91969 3.81986 2.66683 4.43031 2.66683 5.06683V10.9335C2.66683 12.2602 3.74016 13.3335 5.06683 13.3335H10.9335C11.57 13.3335 12.1805 13.0806 12.6306 12.6306C13.0806 12.1805 13.3335 11.57 13.3335 10.9335V5.06683C13.3335 3.74016 12.2602 2.66683 10.9335 2.66683H5.06683ZM11.5002 3.66683C11.7212 3.66683 11.9331 3.75463 12.0894 3.91091C12.2457 4.06719 12.3335 4.27915 12.3335 4.50016C12.3335 4.72118 12.2457 4.93314 12.0894 5.08942C11.9331 5.2457 11.7212 5.3335 11.5002 5.3335C11.2791 5.3335 11.0672 5.2457 10.9109 5.08942C10.7546 4.93314 10.6668 4.72118 10.6668 4.50016C10.6668 4.27915 10.7546 4.06719 10.9109 3.91091C11.0672 3.75463 11.2791 3.66683 11.5002 3.66683ZM8.00016 4.66683C8.88422 4.66683 9.73206 5.01802 10.3572 5.64314C10.9823 6.26826 11.3335 7.11611 11.3335 8.00016C11.3335 8.88422 10.9823 9.73206 10.3572 10.3572C9.73206 10.9823 8.88422 11.3335 8.00016 11.3335C7.11611 11.3335 6.26826 10.9823 5.64314 10.3572C5.01802 9.73206 4.66683 8.88422 4.66683 8.00016C4.66683 7.11611 5.01802 6.26826 5.64314 5.64314C6.26826 5.01802 7.11611 4.66683 8.00016 4.66683ZM8.00016 6.00016C7.46973 6.00016 6.96102 6.21088 6.58595 6.58595C6.21088 6.96102 6.00016 7.46973 6.00016 8.00016C6.00016 8.5306 6.21088 9.0393 6.58595 9.41438C6.96102 9.78945 7.46973 10.0002 8.00016 10.0002C8.5306 10.0002 9.0393 9.78945 9.41438 9.41438C9.78945 9.0393 10.0002 8.5306 10.0002 8.00016C10.0002 7.46973 9.78945 6.96102 9.41438 6.58595C9.0393 6.21088 8.5306 6.00016 8.00016 6.00016Z"
                  fill="var(--c-primary)"
                />
              </svg>
            </nuxt-link>
            <nuxt-link
              v-if="vars.linkedinUrl"
              :to="vars.linkedinUrl"
              target="_blank"
              class="n-link tw-group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-primary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.62663 3.33351C4.62645 3.68713 4.48581 4.0262 4.23563 4.27613C3.98546 4.52605 3.64625 4.66636 3.29263 4.66618C2.93901 4.666 2.59994 4.52536 2.35001 4.27518C2.10009 4.02501 1.95978 3.6858 1.95996 3.33218C1.96014 2.97856 2.10078 2.63949 2.35096 2.38956C2.60113 2.13964 2.94034 1.99934 3.29396 1.99951C3.64758 1.99969 3.98665 2.14033 4.23658 2.39051C4.4865 2.64068 4.6268 2.97989 4.62663 3.33351ZM4.66663 5.65351H1.99996V14.0002H4.66663V5.65351ZM8.87996 5.65351H6.22663V14.0002H8.85329V9.62018C8.85329 7.18018 12.0333 6.95351 12.0333 9.62018V14.0002H14.6666V8.71351C14.6666 4.60018 9.95996 4.75351 8.85329 6.77351L8.87996 5.65351Z"
                  fill="var(--c-primary)"
                />
              </svg>
            </nuxt-link>
            <nuxt-link
              v-if="vars.youtubeUrl"
              :to="vars.youtubeUrl"
              target="_blank"
              class="n-link group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-primary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_23_2087)">
                  <path
                    d="M8.00016 2.6665C8.57016 2.6665 9.15483 2.68117 9.7215 2.70517L10.3908 2.73717L11.0315 2.77517L11.6315 2.81584L12.1795 2.8585C12.7742 2.90402 13.3338 3.1578 13.7598 3.5752C14.1859 3.99259 14.4511 4.54686 14.5088 5.1405L14.5355 5.42384L14.5855 6.0305C14.6322 6.65917 14.6668 7.3445 14.6668 7.99984C14.6668 8.65517 14.6322 9.3405 14.5855 9.96917L14.5355 10.5758C14.5268 10.6732 14.5182 10.7672 14.5088 10.8592C14.4511 11.4529 14.1858 12.0073 13.7596 12.4247C13.3334 12.8421 12.7736 13.0958 12.1788 13.1412L11.6322 13.1832L11.0322 13.2245L10.3908 13.2625L9.7215 13.2945C9.14805 13.3194 8.57415 13.3323 8.00016 13.3332C7.42618 13.3323 6.85227 13.3194 6.27883 13.2945L5.6095 13.2625L4.96883 13.2245L4.36883 13.1832L3.82083 13.1412C3.22613 13.0957 2.66653 12.8419 2.24048 12.4245C1.81443 12.0071 1.54921 11.4528 1.4915 10.8592L1.46483 10.5758L1.41483 9.96917C1.36385 9.31393 1.33673 8.65705 1.3335 7.99984C1.3335 7.3445 1.36816 6.65917 1.41483 6.0305L1.46483 5.42384C1.4735 5.3265 1.48216 5.2325 1.4915 5.1405C1.54919 4.54697 1.81431 3.99278 2.24022 3.5754C2.66614 3.15802 3.22558 2.90417 3.82016 2.8585L4.3675 2.81584L4.9675 2.77517L5.60883 2.73717L6.27816 2.70517C6.85183 2.68026 7.42596 2.66737 8.00016 2.6665ZM8.00016 3.99984C7.45016 3.99984 6.88416 4.0145 6.3335 4.03717L5.6815 4.0685L5.0555 4.10517L4.4675 4.14517L3.92883 4.18717C3.64572 4.20726 3.37886 4.32693 3.17556 4.52499C2.97226 4.72304 2.84564 4.98668 2.81816 5.26917C2.74016 6.07517 2.66683 7.0785 2.66683 7.99984C2.66683 8.92117 2.74016 9.9245 2.81816 10.7305C2.87483 11.3118 3.33616 11.7638 3.92883 11.8125L4.4675 11.8538L5.0555 11.8938L5.6815 11.9312L6.3335 11.9625C6.88416 11.9852 7.45016 11.9998 8.00016 11.9998C8.55016 11.9998 9.11616 11.9852 9.66683 11.9625L10.3188 11.9312L10.9448 11.8945L11.5328 11.8545L12.0715 11.8125C12.3546 11.7924 12.6215 11.6727 12.8248 11.4747C13.0281 11.2766 13.1547 11.013 13.1822 10.7305C13.2602 9.9245 13.3335 8.92117 13.3335 7.99984C13.3335 7.0785 13.2602 6.07517 13.1822 5.26917C13.1547 4.98668 13.0281 4.72304 12.8248 4.52499C12.6215 4.32693 12.3546 4.20726 12.0715 4.18717L11.5328 4.14584L10.9448 4.10584L10.3188 4.0685L9.66683 4.03717C9.11159 4.01328 8.55592 4.00083 8.00016 3.99984ZM6.66683 6.38317C6.66679 6.31801 6.68266 6.25383 6.71307 6.19621C6.74349 6.13858 6.78751 6.08926 6.84133 6.05253C6.89515 6.01579 6.95712 5.99277 7.02187 5.98544C7.08661 5.97812 7.15217 5.98672 7.21283 6.0105L7.26683 6.03717L10.0668 7.65317C10.1226 7.68534 10.1698 7.73049 10.2043 7.78479C10.2389 7.83909 10.2599 7.90094 10.2654 7.96507C10.271 8.0292 10.261 8.09373 10.2363 8.15317C10.2116 8.21261 10.1729 8.26521 10.1235 8.3065L10.0668 8.3465L7.26683 9.96317C7.2104 9.99584 7.14685 10.0142 7.08169 10.0167C7.01654 10.0192 6.95176 10.0058 6.893 9.97749C6.83424 9.94923 6.78328 9.90703 6.74456 9.85457C6.70584 9.80211 6.68053 9.74098 6.67083 9.6765L6.66683 9.6165V6.38317Z"
                    fill="var(--c-primary)"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_23_2087">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </nuxt-link>
            <nuxt-link
              v-if="vars.tiktokUrl"
              :to="vars.tiktokUrl"
              target="_blank"
              class="n-link group tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center !tw-rounded-full tw-bg-white hover:tw-bg-[var(--c-primary)]"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M11.0668 3.88C10.6111 3.35975 10.3599 2.69162 10.3601 2H8.3001V10.2667C8.28422 10.714 8.09535 11.1377 7.77328 11.4486C7.45121 11.7595 7.02107 11.9333 6.57344 11.9333C5.62677 11.9333 4.8401 11.16 4.8401 10.2C4.8401 9.05333 5.94677 8.19333 7.08677 8.54667V6.44C4.78677 6.13333 2.77344 7.92 2.77344 10.2C2.77344 12.42 4.61344 14 6.56677 14C8.6601 14 10.3601 12.3 10.3601 10.2V6.00667C11.1954 6.60657 12.1983 6.92843 13.2268 6.92667V4.86667C13.2268 4.86667 11.9734 4.92667 11.0668 3.88Z"
                  fill="var(--c-primary)"
                />
              </svg>
            </nuxt-link>
          </div>
        </div>
        <div
          class="tw-flex tw-flex-row tw-flex-wrap tw-justify-center tw-gap-14 md:tw-flex-row lg:tw-flex-nowrap lg:tw-justify-start xl:tw-gap-24"
        >
          <div class="tw-flex tw-flex-col-reverse tw-gap-12 sm:tw-flex-row-reverse xl:tw-flex-row xl:tw-gap-24">
            <div
              class="tw-flex tw-min-w-[172px] tw-flex-col tw-items-center tw-gap-5 tw-text-center lg:tw-items-start lg:tw-text-start"
            >
              <h4 class="tw-break-normal">
                {{ vars.columnTitle1Text }}
              </h4>
              <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 lg:tw-items-start">
                <nuxt-link
                  v-for="(column1, index) in columns1"
                  :key="index"
                  :to="column1.link"
                  :external="true"
                  class="n-link tw-flex tw-flex-row tw-items-center tw-gap-2 tw-duration-300 hover:tw-text-[var(--c-primary)]"
                  ><span v-if="column1.icon"><NuxtImg :src="column1.icon" class="tw-w-4 tw-min-w-4" /></span
                  ><span class="tw-opacity-70" >{{ column1.item }}</span></nuxt-link
                >
              </div>
            </div>
            <div
              class="tw-flex tw-min-w-[140px] tw-flex-[1_1_50%] tw-flex-col tw-items-center tw-gap-5 tw-text-center lg:tw-items-start lg:tw-text-start"
            >
              <h4 class="tw-break-normal">
                {{ vars.columnTitle2Text }}
              </h4>
              <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 lg:tw-items-start">
                <nuxt-link
                  v-for="(column2, index) in columns2"
                  :key="`column2-${index}`"
                  :to="column2.link"
                  :external="true"
                  class="n-link tw-flex tw-flex-row tw-items-center tw-gap-2 tw-duration-300 hover:tw-text-[var(--c-primary)]"
                  ><span v-if="column2.icon"><NuxtImg :src="column2.icon" class="tw-w-4 tw-min-w-4" /></span>
                  <span class="tw-opacity-70 ">{{ column2.item }}</span></nuxt-link
                >
              </div>
            </div>
          </div>
          <div
            class="tw-flex tw-max-w-[500px] tw-flex-col tw-items-center tw-gap-1 tw-text-center lg:tw-items-start lg:tw-text-start"
          >
            <small  class="tw-opacity-70">{{ vars.companyTradingAsText }}</small>
            <small  class="tw-opacity-70">{{ vars.companyText }}</small>
            <small  class="tw-opacity-70">Company Number: {{ vars.companyNumber }}</small>
            <small  class="tw-opacity-70">Registered Address: {{ vars.companyAddressText }}</small>
            <small class="tw-opacity-70"> Redress Scheme: {{ vars.companyRedressSchemeText }} </small>
           <small class="tw-opacity-70">
  <nuxt-link
    :external="true"
    :to="vars.companyRedressSchemeLinkUrl"
    class="n-link !tw-text-xs"
    target="_blank"
    rel="noopener"
  >
    {{ vars.companyRedressSchemeLinkUrl }}
  </nuxt-link>
</small>

            <a v-for="(logo, index) in redress" :key="`redressLogos-${index}`" class="n-link">
              <img style="max-height: 42px" :title="logo.title" :alt="logo.title" :src="logo.photo" />
            </a>
          </div>
        </div>
      </div>
    </div>
    <div
      class="tw-z-10 tw-flex tw-flex-wrap tw-items-start tw-justify-around tw-gap-6 tw-bg-[var(--c-primary)] tw-p-6 tw-text-center md:tw-flex-nowrap"
    >
      <small style="font-weight:300 !important;" class="tw-whitespace-nowrap tw-text-start"
        >Powered by
        <span class="tw-font-bold">
          <a
            href="https://www.iceberg-digital.co.uk/neuron/home"
            target="_blank"
            class="n-link"
            style="font-family: var(--f-secondary) !important"
            >Neuron
          </a> </span
        >|
        <span class="tw-font-bold">
          <a
            href="https://www.iceberg-digital.co.uk/"
            target="_blank"
            class="n-link"
            style="font-family: var(--f-secondary) !important"
            >Iceberg Digital
          </a>
        </span>
      </small>
      <small style="font-weight:300 !important;" class="tw-whitespace-nowrap tw-text-sm"
        >© {{ new Date().getFullYear() }} {{ company.name }} All rights reserved.</small
      >
    </div>
  </footer>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import { ComponentMixin } from '~/mixins/component.mixin';
import { useWebsiteStore } from '~/units/website/store';
import { ComponentData } from '~/types/index';

export default defineNuxtComponent({
  name: 'Theme4BaseFooterOne',

  mixins: [ComponentMixin],

  props: { componentData: { type: Object as PropType<ComponentData>, required: true } },

  data() {
    return { variants: { main: 'main', v2: 'v2', v3:'v3', v4:'v4' } };
  },

  computed: {
    ...mapState(useWebsiteStore, ['footerMenuItems']),

    routeUrl() {
      const route = useRoute();
      return route.path;
    },

    columns1() {
      return this.groupedVariables.columns1;
    },

    columns2() {
      return this.groupedVariables.columns2;
    },

    redress() {
      return this.groupedVariables.redress;
    },
  },
});
</script>

<style scoped>
a:hover svg path {
  fill: white;
}
</style>
